import React, { useState, useEffect } from 'react';
import { FileText, Video, Image, Download, Plus, Edit, Trash2, Upload, X, Save } from 'lucide-react';
import {
  fetchConteudos,
  fetchMaterias,
  fetchAnosEscolares,
  createConteudo,
  updateConteudo,
  deleteConteudo,
  incrementDownload,
  formatFileSize,
  getFileType,
  ESTADOS_BRASIL
} from '../services/conteudosService';
import '../styles/conteudos.css';

const Conteudos = () => {
  const [conteudos, setConteudos] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [anosEscolares, setAnosEscolares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConteudo, setEditingConteudo] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    materia_id: '',
    ano_escolar_id: '',
    estado: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Carregar dados iniciais
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [conteudosData, materiasData, anosData] = await Promise.all([
        fetchConteudos(),
        fetchMaterias(),
        fetchAnosEscolares()
      ]);
      setConteudos(conteudosData);
      setMaterias(materiasData);
      setAnosEscolares(anosData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      alert('Erro ao carregar dados. Verifique a conexão com o banco.');
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal para criar/editar
  const openModal = (conteudo = null) => {
    if (conteudo) {
      setEditingConteudo(conteudo);
      setFormData({
        nome: conteudo.nome,
        materia_id: conteudo.materia_id,
        ano_escolar_id: conteudo.ano_escolar_id,
        estado: conteudo.estado
      });
    } else {
      setEditingConteudo(null);
      setFormData({
        nome: '',
        materia_id: '',
        ano_escolar_id: '',
        estado: ''
      });
    }
    setSelectedFile(null);
    setShowModal(true);
  };

  // Fechar modal
  const closeModal = () => {
    setShowModal(false);
    setEditingConteudo(null);
    setFormData({
      nome: '',
      materia_id: '',
      ano_escolar_id: '',
      estado: ''
    });
    setSelectedFile(null);
  };

  // Manipular mudanças no formulário
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Manipular seleção de arquivo
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  // Submeter formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.materia_id || !formData.ano_escolar_id || !formData.estado) {
      alert('Preencha todos os campos obrigatórios.');
      return;
    }

    if (!editingConteudo && !selectedFile) {
      alert('Selecione um arquivo para upload.');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingConteudo) {
        await updateConteudo(editingConteudo.id, formData, selectedFile);
      } else {
        await createConteudo(formData, selectedFile);
      }
      
      await loadData();
      closeModal();
      alert(editingConteudo ? 'Conteúdo atualizado com sucesso!' : 'Conteúdo criado com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar conteúdo:', error);
      alert('Erro ao salvar conteúdo. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Deletar conteúdo
  const handleDelete = async (conteudo) => {
    if (!window.confirm(`Tem certeza que deseja deletar "${conteudo.nome}"?`)) {
      return;
    }

    try {
      await deleteConteudo(conteudo.id, conteudo.arquivo_url);
      await loadData();
      alert('Conteúdo deletado com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar conteúdo:', error);
      alert('Erro ao deletar conteúdo. Tente novamente.');
    }
  };

  // Fazer download e incrementar contador
  const handleDownload = async (conteudo) => {
    if (!conteudo.arquivo_url) {
      alert('Arquivo não disponível.');
      return;
    }

    try {
      await incrementDownload(conteudo.id);
      window.open(conteudo.arquivo_url, '_blank');
      // Atualizar a lista para refletir o novo contador
      await loadData();
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      // Mesmo com erro, permitir o download
      window.open(conteudo.arquivo_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="conteudos">
        <div className="page-header">
          <h1 className="page-title">Conteúdos</h1>
          <p className="page-subtitle">Carregando...</p>
        </div>
      </div>
    );
  }

  const totalDownloads = conteudos.reduce((sum, c) => sum + (c.downloads || 0), 0);

  return (
    <div className="conteudos">
      <div className="page-header">
        <h1 className="page-title">Conteúdos</h1>
        <p className="page-subtitle">Gerenciamento de materiais didáticos</p>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <Plus size={18} />
          Novo Conteúdo
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <FileText size={24} />
            </div>
          </div>
          <div className="stat-value">{conteudos.length}</div>
          <div className="stat-label">Total de Conteúdos</div>
        </div>
        <div className="stat-card success">
          <div className="stat-header">
            <div className="stat-icon success">
              <Download size={24} />
            </div>
          </div>
          <div className="stat-value">{totalDownloads}</div>
          <div className="stat-label">Total de Downloads</div>
        </div>
      </div>

      <div className="content-card">
        {conteudos.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h3>Nenhum conteúdo encontrado</h3>
            <p>Clique em "Novo Conteúdo" para adicionar o primeiro material.</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Matéria</th>
                <th>Ano</th>
                <th>Estado</th>
                <th>Tamanho</th>
                <th>Downloads</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {conteudos.map(conteudo => (
                <tr key={conteudo.id}>
                  <td>
                    <div className="table-cell-content">
                      <div className="cell-icon">
                        {getFileType(conteudo.arquivo_nome || '') === 'Vídeo' && <Video size={18} />}
                        {['PDF', 'Word', 'Excel', 'PowerPoint', 'Texto'].includes(getFileType(conteudo.arquivo_nome || '')) && <FileText size={18} />}
                        {getFileType(conteudo.arquivo_nome || '') === 'Imagem' && <Image size={18} />}
                      </div>
                      <span>{conteudo.nome}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getFileType(conteudo.arquivo_nome || '').toLowerCase()}`}>
                      {getFileType(conteudo.arquivo_nome || '')}
                    </span>
                  </td>
                  <td>{conteudo.materias?.nome}</td>
                  <td>{conteudo.anos_escolares?.nome}</td>
                  <td>{conteudo.estado}</td>
                  <td>{conteudo.arquivo_tamanho ? formatFileSize(conteudo.arquivo_tamanho) : '-'}</td>
                  <td>{conteudo.downloads || 0}</td>
                  <td>
                    <div className="action-buttons">
                      {conteudo.arquivo_url && (
                        <button 
                          className="btn-icon" 
                          onClick={() => handleDownload(conteudo)}
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                      )}
                      <button 
                        className="btn-icon" 
                        onClick={() => openModal(conteudo)}
                        title="Editar"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="btn-icon danger" 
                        onClick={() => handleDelete(conteudo)}
                        title="Deletar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal para criar/editar conteúdo */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingConteudo ? 'Editar Conteúdo' : 'Novo Conteúdo'}</h2>
              <button className="btn-icon" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label htmlFor="nome">Nome *</label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  placeholder="Digite o nome do conteúdo"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="materia_id">Matéria *</label>
                  <select
                    id="materia_id"
                    name="materia_id"
                    value={formData.materia_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione uma matéria</option>
                    {materias.map(materia => (
                      <option key={materia.id} value={materia.id}>
                        {materia.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="ano_escolar_id">Ano Escolar *</label>
                  <select
                    id="ano_escolar_id"
                    name="ano_escolar_id"
                    value={formData.ano_escolar_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione um ano</option>
                    {anosEscolares.map(ano => (
                      <option key={ano.id} value={ano.id}>
                        {ano.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="estado">Estado *</label>
                <select
                  id="estado"
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Selecione um estado</option>
                  {ESTADOS_BRASIL.map(estado => (
                    <option key={estado} value={estado}>
                      {estado}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="arquivo">
                  Arquivo {!editingConteudo && '*'}
                </label>
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    id="arquivo"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mpeg,.mov,.avi,.mp3,.wav,.ogg"
                  />
                  <div className="file-input-display">
                    <Upload size={20} />
                    <span>
                      {selectedFile ? selectedFile.name : 
                       editingConteudo ? 'Selecione um novo arquivo (opcional)' : 
                       'Clique para selecionar um arquivo'}
                    </span>
                  </div>
                </div>
                {selectedFile && (
                  <div className="file-info">
                    <small>
                      Tipo: {getFileType(selectedFile.name)} | 
                      Tamanho: {formatFileSize(selectedFile.size)}
                    </small>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  <Save size={18} />
                  {submitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Conteudos;
